import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { formatAddress, formatETH } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { EmailSettings } from '../components/EmailSettings';
import { ActiveBoostBadge } from '../components/ActiveBoostBadge';
import { DashboardOverviewTab } from '../components/dashboard/DashboardOverviewTab';
import {
    DashboardAnalyticsTab,
    DashboardActiveBetsTab,
    DashboardHistoryTab,
} from '../components/dashboard/DashboardTabs';
import { DashboardAchievementsTab } from '../components/dashboard/DashboardAchievementsTab';
import {
    Wallet, Plus, Activity, BarChart3, Timer, History, Settings, Sparkles, Download,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function DashboardConnectPrompt({ onConnect }) {
    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="p-16 rounded-2xl bg-white/[0.02] border border-white/5 text-center max-w-lg mx-auto">
                <div className="w-20 h-20 rounded-full bg-[#00FF66]/10 flex items-center justify-center mx-auto mb-6">
                    <Wallet className="w-10 h-10 text-[#00FF66]" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Connect Your Wallet</h2>
                <p className="text-white/50 mb-8">
                    Connect your wallet to view your advanced betting dashboard with detailed statistics.
                </p>
                <Button
                    onClick={onConnect}
                    className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_20px_rgba(130,255,122,0.3)] font-semibold px-8 py-6 text-lg"
                    data-testid="dashboard-connect-wallet"
                >
                    <Wallet className="w-5 h-5 mr-2" />
                    Connect Wallet
                </Button>
            </div>
        </div>
    );
}

function DashboardHeader({ address, chainName, balance, b4pData }) {
    return (
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 animate-fade-in-down">
            <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2 text-white">
                    <span className="text-[#00FF66]">Dashboard</span> V3
                </h1>
                <div className="flex items-center gap-3 text-white/50">
                    <span className="font-mono">{formatAddress(address)}</span>
                    <span className="text-white/20">|</span>
                    <span className="px-2 py-1 rounded bg-[#6F3FCA]/20 text-xs font-mono text-[#9B6FE8]">{chainName}</span>
                </div>
                <div className="mt-3">
                    <ActiveBoostBadge address={address} />
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 animate-fade-in-up delay-1">
                    <div className="text-xs text-white/40 mb-1">Wallet Balance</div>
                    <div className="font-mono text-2xl text-white font-bold stat-number">{formatETH(balance)} ETH</div>
                </div>
                {b4pData && (
                    <Link to="/b4p" className="p-4 rounded-xl bg-[#FFD700]/5 border border-[#FFD700]/20 hover:border-[#FFD700]/40 transition-colors animate-fade-in-up delay-2 card-hover" data-testid="dashboard-b4p-card">
                        <div className="text-xs text-[#FFD700]/60 mb-1">B4P Balance</div>
                        <div className="font-mono text-2xl text-[#FFD700] font-bold stat-number">
                            {b4pData.balance.toLocaleString()} <span className="text-sm">B4P</span>
                        </div>
                    </Link>
                )}
                <Link to="/create">
                    <Button className="bg-[#00FF66] text-[#0A0A0C] hover:bg-[#00CC52] hover:shadow-[0_0_20px_rgba(130,255,122,0.3)] font-semibold h-full py-4 btn-press">
                        <Plus className="w-5 h-5 mr-2" />
                        Create Bet
                    </Button>
                </Link>
                <a href={`${API_URL}/api/users/${address}/export-csv`} download>
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 h-full py-4 btn-press" data-testid="export-csv-btn">
                        <Download className="w-5 h-5 mr-2" />
                        CSV
                    </Button>
                </a>
            </div>
        </div>
    );
}

export function DashboardPage() {
    const { address, isConnected, connect, balance, chainName } = useWeb3();
    const [stats, setStats] = useState(null);
    const [gamification, setGamification] = useState(null);
    const [b4pData, setB4pData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        async function fetchData() {
            if (!address) {
                setLoading(false);
                return;
            }
            try {
                const response = await fetch(`${API_URL}/api/users/${address}/advanced-stats`);
                if (response.ok) setStats(await response.json());

                const gamificationRes = await fetch(`${API_URL}/api/gamification/${address}`);
                if (gamificationRes.ok) setGamification(await gamificationRes.json());

                fetch(`${API_URL}/api/gamification/${address}/check-badges`, { method: 'POST' });

                const b4pRes = await fetch(`${API_URL}/api/b4p/balance/${address}`);
                if (b4pRes.ok) setB4pData(await b4pRes.json());
            } catch (error) {
                console.error('Failed to fetch dashboard data:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [address]);

    if (!isConnected) return <DashboardConnectPrompt onConnect={connect} />;

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="dashboard-page">
            <DashboardHeader address={address} chainName={chainName} balance={balance} b4pData={b4pData} />

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
                <TabsList className="bg-white/5 border border-white/10">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]">
                        <Activity className="w-4 h-4 mr-2" />Overview
                    </TabsTrigger>
                    <TabsTrigger value="analytics" className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]">
                        <BarChart3 className="w-4 h-4 mr-2" />Analytics
                    </TabsTrigger>
                    <TabsTrigger value="active" className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]">
                        <Timer className="w-4 h-4 mr-2" />Active Bets
                    </TabsTrigger>
                    <TabsTrigger value="history" className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]">
                        <History className="w-4 h-4 mr-2" />History
                    </TabsTrigger>
                    <TabsTrigger value="settings" className="data-[state=active]:bg-[#00FF66]/10 data-[state=active]:text-[#00FF66]">
                        <Settings className="w-4 h-4 mr-2" />Settings
                    </TabsTrigger>
                    <TabsTrigger value="achievements" className="data-[state=active]:bg-[#FFD700]/10 data-[state=active]:text-[#FFD700]">
                        <Sparkles className="w-4 h-4 mr-2" />Achievements
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-6">
                    <DashboardOverviewTab stats={stats} loading={loading} />
                </TabsContent>
                <TabsContent value="analytics" className="mt-6">
                    <DashboardAnalyticsTab stats={stats} loading={loading} />
                </TabsContent>
                <TabsContent value="active" className="mt-6">
                    <DashboardActiveBetsTab stats={stats} loading={loading} />
                </TabsContent>
                <TabsContent value="history" className="mt-6">
                    <DashboardHistoryTab />
                </TabsContent>
                <TabsContent value="settings" className="mt-6">
                    <div className="max-w-md">
                        <EmailSettings />
                    </div>
                </TabsContent>
                <TabsContent value="achievements" className="mt-6">
                    <DashboardAchievementsTab gamification={gamification} loading={loading} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
